import type { AmplitudeEventKeys } from '../../amplitude-properties.types';
import { getEventPropsBuilder, toSnakeCase } from '../../utils';
import type { TrackingEvent } from '@/tracking/tracking-manager';

type GameInfoVideoData = {
    videoTitle: string;
    gameId: string;
    gameName: string;
    totalDuration: number;
    pageName: string;
};

const PAGE_NAME_ALIASES: Record<string, string> = {
    bonus_spins_details: 'bonus_spins_detail',
    promo_offer: 'rewards',
    promotions: 'rewards',
    reward_machine_exit_screen: 'reward_machine_exit'
};

export const getVideoTitleFromUrl = (url: unknown): string => {
    if (typeof url !== 'string') {
        return '';
    }

    const [path = ''] = url.trim().split(/[?#]/, 1);
    const fileName = path.slice(path.lastIndexOf('/') + 1);
    if (!fileName) {
        return '';
    }

    let decodedFileName = fileName;
    try {
        decodedFileName = decodeURIComponent(fileName);
    } catch {
        // Keep the original filename when the URL contains malformed escape sequences.
    }

    const extensionIndex = decodedFileName.lastIndexOf('.');
    return extensionIndex === -1 ? decodedFileName : decodedFileName.slice(0, extensionIndex);
};

export const getGameInfoVideoPageName = ({
    pageName,
    module,
    zoneName
}: {
    pageName?: string;
    module?: string;
    zoneName?: string;
}): string => {
    const normalizedModule = toSnakeCase(module ?? '');
    const normalizedZoneName = toSnakeCase(zoneName ?? '');

    if (normalizedModule.startsWith('search_') || normalizedZoneName.startsWith('search_')) {
        return 'search';
    }

    const normalizedPageName = toSnakeCase(pageName || 'home');
    return PAGE_NAME_ALIASES[normalizedPageName] ?? normalizedPageName;
};

const buildEventProps = getEventPropsBuilder(
    (data: GameInfoVideoData): Partial<AmplitudeEventKeys> => ({
        event: 'Game Info Video Auto Play Successful',
        Module: 'game_info_modal',
        'Video Title': toSnakeCase(data.videoTitle),
        'Game ID': toSnakeCase(data.gameId),
        'Game Name': toSnakeCase(data.gameName),
        'Total Video Duration': Math.round(data.totalDuration),
        'Page Name': getGameInfoVideoPageName({ pageName: data.pageName })
    })
);

export const trackGameInfoVideoAutoPlay = (data: GameInfoVideoData): { event: TrackingEvent } => ({
    event: {
        destination: 'amplitude',
        eventProps: buildEventProps(data)
    }
});
